import { useState, useEffect } from 'react';
import { Folder, FolderInput, ChevronDown, Check, Plus, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { apiFetch } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import AuthModal from './AuthModal';

interface FolderItem {
  id: string;
  name: string;
  item_count: number;
}

interface FolderSelectorProps {
  selectedFolderId: string | null;
  onSelectFolder: (folderId: string | null) => void;
  disabled?: boolean;
  compact?: boolean; // For inline use in hero input
}

export function FolderSelector({
  selectedFolderId,
  onSelectFolder,
  disabled = false,
  compact = false,
}: FolderSelectorProps) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [folders, setFolders] = useState<FolderItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingInProgress, setCreatingInProgress] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);

  // For guests, show auth modal instead of folder picker
  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen && !user) {
      setShowAuthModal(true);
      return;
    }
    setOpen(newOpen);
  };

  // Fetch folders when popover opens
  useEffect(() => {
    if (open && folders.length === 0) {
      fetchFolders();
    }
  }, [open]);

  const fetchFolders = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<{ folders: FolderItem[] } | FolderItem[]>('/folders');
      const list = Array.isArray(data) ? data : (data as { folders: FolderItem[] }).folders || [];
      setFolders(list);
    } catch (err) {
      setError('Failed to load folders');
      console.error('Failed to fetch folders:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim() || creatingInProgress) return;
    
    setCreatingInProgress(true);
    try {
      const result = await apiFetch<{ id: string; name: string }>('/folders', {
        method: 'POST',
        body: JSON.stringify({ name: newFolderName.trim() }),
      });
      
      const newFolder: FolderItem = {
        id: result.id,
        name: result.name,
        item_count: 0,
      };
      
      setFolders(prev => [newFolder, ...prev]);
      onSelectFolder(newFolder.id);
      setNewFolderName('');
      setCreating(false);
      setOpen(false);
    } catch (err) {
      console.error('Failed to create folder:', err);
    } finally {
      setCreatingInProgress(false);
    }
  };

  const selectedFolder = folders.find(f => f.id === selectedFolderId);
  const displayName = selectedFolder?.name || 'Auto';

  if (compact) {
    // Compact mode: just a folder icon button
    return (
      <>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            className={`flex items-center justify-center h-14 w-12 rounded-lg border-2 transition-colors ${
              selectedFolderId
                ? 'border-[#0066FF] bg-blue-50 text-[#0066FF]'
                : 'border-gray-300 bg-gray-50 text-gray-500 hover:border-gray-400'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            title={selectedFolder ? `Saving to: ${selectedFolder.name}` : 'Select folder (optional)'}
          >
            <FolderInput className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-2" align="start" side="bottom">
          <FolderList
            folders={folders}
            loading={loading}
            error={error}
            selectedFolderId={selectedFolderId}
            onSelect={(id) => {
              onSelectFolder(id);
              setOpen(false);
            }}
            creating={creating}
            setCreating={setCreating}
            newFolderName={newFolderName}
            setNewFolderName={setNewFolderName}
            onCreateFolder={handleCreateFolder}
            creatingInProgress={creatingInProgress}
          />
        </PopoverContent>
      </Popover>
      {showAuthModal && (
        <AuthModal
          initialTab="signup"
          onClose={() => setShowAuthModal(false)}
        />
      )}
      </>
    );
  }

  // Full mode: button with label
  return (
    <>
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={`flex items-center gap-2 h-10 px-3 ${
            selectedFolderId ? 'border-[#0066FF] text-[#0066FF]' : ''
          }`}
        >
          <FolderInput className="w-4 h-4" strokeWidth={1.5} />
          <span className="max-w-[120px] truncate">{displayName}</span>
          <ChevronDown className="w-4 h-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2" align="start" side="bottom">
        <FolderList
          folders={folders}
          loading={loading}
          error={error}
          selectedFolderId={selectedFolderId}
          onSelect={(id) => {
            onSelectFolder(id);
            setOpen(false);
          }}
          creating={creating}
          setCreating={setCreating}
          newFolderName={newFolderName}
          setNewFolderName={setNewFolderName}
          onCreateFolder={handleCreateFolder}
          creatingInProgress={creatingInProgress}
        />
      </PopoverContent>
    </Popover>
    {showAuthModal && (
      <AuthModal
        initialTab="signup"
        onClose={() => setShowAuthModal(false)}
      />
    )}
    </>
  );
}

interface FolderListProps {
  folders: FolderItem[];
  loading: boolean;
  error: string | null;
  selectedFolderId: string | null;
  onSelect: (id: string | null) => void;
  creating: boolean;
  setCreating: (v: boolean) => void;
  newFolderName: string;
  setNewFolderName: (v: string) => void;
  onCreateFolder: () => void;
  creatingInProgress: boolean;
}

function FolderList({
  folders,
  loading,
  error,
  selectedFolderId,
  onSelect,
  creating,
  setCreating,
  newFolderName,
  setNewFolderName,
  onCreateFolder,
  creatingInProgress,
}: FolderListProps) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-4 text-sm text-gray-500">
        Loading...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-4 text-sm text-red-500">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <p className="px-2 py-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
        Save to folder
      </p>
      
      {/* Auto option - default behavior */}
      <button
        type="button"
        onClick={() => onSelect(null)}
        className={`w-full flex items-center gap-2 px-2 py-2 rounded-md text-sm text-left transition-colors ${
          selectedFolderId === null
            ? 'bg-blue-50 text-[#0066FF]'
            : 'hover:bg-gray-100 text-gray-700'
        }`}
      >
        <Folder className="w-4 h-4 opacity-70" strokeWidth={1.5} />
        <span className="flex-1">Auto (by channel)</span>
        {selectedFolderId === null && <Check className="w-4 h-4" />}
      </button>

      {/* Existing folders */}
      {folders.map(folder => (
        <button
          key={folder.id}
          type="button"
          onClick={() => onSelect(folder.id)}
          className={`w-full flex items-center gap-2 px-2 py-2 rounded-md text-sm text-left transition-colors ${
            selectedFolderId === folder.id
              ? 'bg-blue-50 text-[#0066FF]'
              : 'hover:bg-gray-100 text-gray-700'
          }`}
        >
          <Folder className="w-4 h-4 opacity-70" strokeWidth={1.5} />
          <span className="flex-1 truncate">{folder.name}</span>
          <span className="text-xs text-gray-400">{folder.item_count}</span>
          {selectedFolderId === folder.id && <Check className="w-4 h-4" />}
        </button>
      ))}

      {/* Divider */}
      <div className="border-t border-gray-100 my-2" />

      {/* Create new folder */}
      {creating ? (
        <div className="flex items-center gap-2 px-2">
          <Input
            autoFocus
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onCreateFolder();
              if (e.key === 'Escape') {
                setCreating(false);
                setNewFolderName('');
              }
            }}
            placeholder="Folder name..."
            className="h-8 text-sm"
            disabled={creatingInProgress}
          />
          <button
            type="button"
            onClick={onCreateFolder}
            disabled={creatingInProgress || !newFolderName.trim()}
            className="p-1.5 rounded hover:bg-gray-100 text-[#0066FF] disabled:opacity-50"
          >
            <Check className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => {
              setCreating(false);
              setNewFolderName('');
            }}
            className="p-1.5 rounded hover:bg-gray-100 text-gray-400"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="w-full flex items-center gap-2 px-2 py-2 rounded-md text-sm text-left hover:bg-gray-100 text-[#0066FF]"
        >
          <Plus className="w-4 h-4" strokeWidth={1.5} />
          <span>Create new folder</span>
        </button>
      )}
    </div>
  );
}

export default FolderSelector;
